import { useState, useEffect } from "react";
import { api, apiError } from "../lib/api";
import { UNITS, SHIPPING_TYPES, orderItems } from "../lib/constants";
import { toast } from "sonner";
import { X, Plus, Trash2 } from "lucide-react";
import { ProductCombobox } from "./ProductCombobox";
import { CustomerCombobox } from "./CustomerCombobox";

export function OrderEditModal({ order, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    customer_id: order.customer_id || null,
    company_name: order.customer_snapshot?.company_name || "",
    phone: order.customer_snapshot?.phone || "",
    whatsapp_phone: order.whatsapp_phone || "",
    delivery_date: order.delivery_date || "",
    shipping_type: order.shipping_type || "Ambar",
    note: order.note || "",
    items: orderItems(order).map((it) => ({
      product_id: it.product_id || null,
      product_code: it.product_code || "",
      product_name: it.product_name || "",
      quantity: it.quantity ?? "",
      unit: it.unit || "Litre",
    })),
  }));
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const pickCustomerObj = (c) => setForm((f) => ({ ...f, customer_id: c.id, company_name: c.company_name, phone: c.phone, whatsapp_phone: c.whatsapp_phone || c.phone }));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (idx, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));
  const selectProduct = (idx, p) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, product_id: p.id, product_code: p.product_code, product_name: p.product_name, unit: p.unit || it.unit } : it)) }));
  const invalidateProduct = (idx, field, text) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, product_id: null, [field === "code" ? "product_code" : "product_name"]: text } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: null, product_code: "", product_name: "", quantity: "", unit: "Litre" }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    const unselected = form.items.filter((it) => (it.product_code || it.product_name) && !it.product_id);
    if (unselected.length > 0) {
      toast.error("Lütfen ürünleri listeden seçin. Katalogda olmayan ürün kaydedilemez.");
      return;
    }
    const items = form.items
      .filter((it) => it.product_id && it.quantity)
      .map((it) => ({ product_id: it.product_id, product_code: it.product_code, product_name: it.product_name, quantity: parseFloat(it.quantity), unit: it.unit }));
    if (!form.company_name || !form.phone || items.length === 0) {
      toast.error("Firma, telefon ve en az bir seçili ürün zorunludur");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/orders/${order.id}`, {
        customer_id: form.customer_id, company_name: form.company_name, phone: form.phone, whatsapp_phone: form.whatsapp_phone,
        delivery_date: form.delivery_date, shipping_type: form.shipping_type, note: form.note, items,
      });
      toast.success(`#${order.order_no} güncellendi`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const ic = "w-full rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary";
  const lc = "mb-1 block text-xs font-semibold text-muted-foreground";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="order-edit-modal">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-border bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-border p-5">
          <h3 className="font-display text-lg font-bold">Sipariş Düzenle · <span className="font-mono text-primary">{order.order_no}</span></h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={lc}>Firma *</label>
              <CustomerCombobox testid="edit-company" value={form.company_name} selectedId={form.customer_id}
                customers={customers.filter((c) => c.is_active !== false)} onPick={pickCustomerObj}
                onType={(t) => setForm((f) => ({ ...f, company_name: t, customer_id: null }))} />
            </div>
            <div><label className={lc}>Telefon *</label><input data-testid="edit-phone" className={ic} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className={`${lc} mb-0`}>Ürünler *</label>
              <span className="text-xs text-muted-foreground">{form.items.length} kalem</span>
            </div>
            <div className="space-y-3" data-testid="edit-items">
              {form.items.map((it, idx) => (
                <div key={idx} data-testid={`edit-item-${idx}`} className="rounded-xl border border-border bg-secondary/30 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-bold">Ürün {idx + 1}</span>
                    {form.items.length > 1 && (
                      <button type="button" data-testid={`edit-remove-item-${idx}`} onClick={() => removeItem(idx)} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-500/10">
                        <Trash2 className="h-3.5 w-3.5" /> Kaldır
                      </button>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ProductCombobox item={it} field="code" testid={`edit-item-code-${idx}`} onSelect={(p) => selectProduct(idx, p)} onInvalidate={(t) => invalidateProduct(idx, "code", t)} />
                    <ProductCombobox item={it} field="name" testid={`edit-item-name-${idx}`} onSelect={(p) => selectProduct(idx, p)} onInvalidate={(t) => invalidateProduct(idx, "name", t)} />
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <input data-testid={`edit-item-quantity-${idx}`} type="number" step="any" placeholder="Miktar *" className={ic} value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} />
                    <select data-testid={`edit-item-unit-${idx}`} className={ic} value={it.unit} onChange={(e) => setItem(idx, "unit", e.target.value)}>
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" data-testid="edit-add-item-btn" onClick={addItem} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 py-2.5 text-sm font-semibold text-primary hover:bg-primary/5">
              <Plus className="h-4 w-4" /> Ürün Ekle
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className={lc}>Teslim Tarihi</label><input type="date" className={ic} value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} /></div>
            <div><label className={lc}>Sevk Şekli</label><select className={ic} value={form.shipping_type} onChange={(e) => set("shipping_type", e.target.value)}>{SHIPPING_TYPES.map((s) => <option key={s}>{s}</option>)}</select></div>
          </div>
          <div><label className={lc}>Not</label><textarea rows={2} className={ic} value={form.note} onChange={(e) => set("note", e.target.value)} /></div>
        </div>

        <div className="border-t border-border p-5">
          <button data-testid="edit-save-btn" onClick={save} disabled={saving} className="w-full rounded-xl bg-primary py-3 font-semibold text-white hover:brightness-110 disabled:opacity-50">
            {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}
